import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { URGENCY, daysLabel, formatDate, reminderReached, urgencyOf } from "@/lib/dates";
import { fmtDecimalHours, fmtMoney } from "@/lib/time";
import { TimeSection, ExpenseSection } from "@/components/TimeExpenseSections";
import ExternalLinks from "@/components/ExternalLinks";
import CalendarDialog from "@/components/CalendarDialog";
import { useT, useDaysLabel } from "@/i18n";
import { Button } from "@/components/ui/button";
import { MatterDialog, DeadlineDialog, TaskDialog } from "@/components/forms";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, ExternalLink, Pencil, Plus, Trash2, Check, BellRing, Landmark, CalendarPlus,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  Active: "bg-green-50 text-green-700 border-green-200",
  Pending: "bg-amber-50 text-amber-800 border-amber-200",
  Closed: "bg-slate-100 text-slate-500 border-slate-200",
};

const PRIORITY_BADGE = {
  High: "bg-red-50 text-red-800 border-red-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};

const DEADLINE_TYPES = ["Procedural deadline", "Court hearing", "Client deadline", "Internal deadline", "Other"];

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 mt-1 break-words">{children || "—"}</div>
    </div>
  );
}

function ItemRow({ item, kind, onEdit, onDelete, onToggle, onCalendar }) {
  const { t } = useT();
  const daysLbl = useDaysLabel();
  const urg = urgencyOf(item.due_date, item.status);
  const u = URGENCY[urg];
  const completed = item.status === "Completed";
  const hasReminder = kind === "deadline" && !completed && reminderReached(item.reminder_dates, item.created_at);
  return (
    <div
      data-testid={`${kind}-row-${item.id}`}
      className={`bg-white border border-slate-200 border-l-4 ${u.bar} rounded-md px-5 py-4 ${completed ? "opacity-70" : ""}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className={`text-sm font-semibold text-slate-800 ${completed ? "line-through" : ""}`}>
            {item.description}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
            <span className={`px-2 py-0.5 rounded-full border font-semibold ${u.badge}`} data-testid={`${kind}-status-badge-${urg}-${item.id}`}>
              {completed ? t("items.completed") : daysLbl(item.due_date)}
            </span>
            <span className={`px-2 py-0.5 rounded-full border ${PRIORITY_BADGE[item.priority] || ""}`}>
              {t(`val.${item.priority}`)}
            </span>
            {kind === "deadline" && item.deadline_type && (
              <span
                className="px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200"
                data-testid={`deadline-type-badge-${item.id}`}
              >
                {t(`val.${item.deadline_type}`)}
              </span>
            )}
            {hasReminder && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200" data-testid={`${kind}-reminder-badge-${item.id}`}>
                <BellRing className="h-3 w-3" /> {t("deadlines.reminder_due")}
              </span>
            )}
            <span className="text-slate-500">{formatDate(item.due_date)}</span>
            {kind === "deadline" && item.start_time && (
              <span className="text-slate-500" data-testid={`deadline-time-${item.id}`}>
                {item.start_time}{item.end_time ? `–${item.end_time}` : ""}
              </span>
            )}
          </div>
          {kind === "deadline" && item.reminder_dates?.length > 0 && (
            <div className="text-xs text-slate-500 mt-1.5">
              {t("deadlines.reminders")}: {item.reminder_dates.map(formatDate).join(", ")}
            </div>
          )}
          {item.notes && <div className="text-xs text-slate-500 mt-1.5">{item.notes}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {kind === "deadline" && (
            <button
              data-testid={`deadline-calendar-${item.id}`}
              onClick={() => onCalendar(item)}
              title={t("cal.add")}
              className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-slate-800 transition-colors"
            >
              <CalendarPlus className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            data-testid={`${kind}-toggle-${item.id}`}
            onClick={() => onToggle(item)}
            title={completed ? t("items.reopen") : t("items.mark_completed")}
            className={`p-1.5 rounded-md border transition-colors ${
              completed
                ? "border-slate-200 text-slate-400 hover:text-slate-700"
                : "border-green-200 text-green-700 hover:bg-green-50"
            }`}
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            data-testid={`${kind}-edit-${item.id}`}
            onClick={() => onEdit(item)}
            className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-slate-800 transition-colors"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            data-testid={`${kind}-delete-${item.id}`}
            onClick={() => onDelete(item)}
            className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-red-700 hover:border-red-200 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MatterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [editMatter, setEditMatter] = useState(false);

  const changeStatus = async (status, confirmKey, doneKey) => {
    if (!window.confirm(t(confirmKey))) return;
    try {
      await api.put(`/matters/${matter.id}`, { ...matter, status });
      toast.success(t(doneKey));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const [deadlineDialog, setDeadlineDialog] = useState({ open: false, item: null });
  const [taskDialog, setTaskDialog] = useState({ open: false, item: null });
  const [deadlineTypeFilter, setDeadlineTypeFilter] = useState("All");
  const [calDeadline, setCalDeadline] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const load = () => {
    api
      .get(`/matters/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  };

  useEffect(load, [id]);

  if (error) {
    return (
      <div className="text-sm text-red-700" data-testid="matter-detail-error">
        {error} — <Link to="/matters" className="underline">{t("matter.error_back")}</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="text-sm text-slate-500" data-testid="matter-detail-loading">{t("common.loading")}</div>;
  }

  const { matter, deadlines, tasks } = data;
  const totals = data.totals || {
    total_billable_minutes: 0, value_billable_time: 0, unbilled_time_value: 0,
    total_billable_expenses: 0, unbilled_expenses: 0,
  };
  const visibleDeadlines =
    deadlineTypeFilter === "All" ? deadlines : deadlines.filter((d) => d.deadline_type === deadlineTypeFilter);

  const toggleItem = async (kind, item) => {
    const endpoint = kind === "deadline" ? `/deadlines/${item.id}` : `/tasks/${item.id}`;
    try {
      await api.put(endpoint, { ...item, status: item.status === "Completed" ? "Open" : "Completed" });
      load();
      toast.success(item.status === "Completed" ? t("items.reopened") : t("items.completed_toast"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteItem = async (kind, item) => {
    if (!window.confirm(t(kind === "deadline" ? "deadlines.delete_confirm" : "tasks.delete_confirm"))) return;
    const endpoint = kind === "deadline" ? `/deadlines/${item.id}` : `/tasks/${item.id}`;
    try {
      await api.delete(endpoint);
      load();
      toast.success(t(kind === "deadline" ? "deadlines.deleted" : "tasks.deleted"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteMatter = async () => {
    if (!window.confirm(t("matter.delete_confirm"))) return;
    try {
      await api.delete(`/matters/${matter.id}`);
      toast.success(t("matter.deleted"));
      navigate("/matters");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const dropboxHref = matter.dropbox_link
    ? /^https?:\/\//i.test(matter.dropbox_link)
      ? matter.dropbox_link
      : `https://${matter.dropbox_link}`
    : null;

  return (
    <div data-testid="matter-detail-page" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Link
            to="/matters"
            data-testid="back-to-matters-link"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t("matter.back")}
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-serif text-3xl tracking-tight text-slate-900">{matter.name}</h1>
            <span
              data-testid="matter-detail-status-badge"
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${STATUS_BADGE[matter.status] || ""}`}
            >
              {t(`val.${matter.status}`)}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {matter.client}
            {matter.internal_ref ? ` · ${t("matter.internal_ref")} ${matter.internal_ref}` : ""}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            data-testid="create-invoice-from-matter-button"
            onClick={() => navigate(`/invoices/new?matter=${matter.id}`)}
          >
            <Plus className="h-4 w-4 mr-1.5" /> {t("matter.create_invoice")}
          </Button>
          <Button
            variant="outline"
            data-testid="edit-matter-button"
            onClick={() => setEditMatter(true)}
          >
            <Pencil className="h-4 w-4 mr-1.5" /> {t("common.edit")}
          </Button>
          {matter.status !== "Closed" && matter.status !== "Archived" && (
            <>
              <Button
                variant="outline"
                data-testid="close-matter-button"
                onClick={() => changeStatus("Closed", "matters.close_confirm", "matters.closed")}
              >
                {t("matters.close")}
              </Button>
              <Button
                variant="outline"
                data-testid="archive-matter-button"
                onClick={() => changeStatus("Archived", "matters.archive_confirm", "matters.archived")}
              >
                {t("matters.archive")}
              </Button>
            </>
          )}
          {(matter.status === "Closed" || matter.status === "Archived") && (
            <Button
              variant="outline"
              data-testid="reopen-matter-button"
              onClick={() => changeStatus("Active", "matters.reopen_confirm", "matters.reopened")}
            >
              {t("matters.reopen")}
            </Button>
          )}
          <Button
            variant="outline"
            data-testid="delete-matter-button"
            onClick={deleteMatter}
            className="text-red-700 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> {t("common.delete")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="matter-details-card">
            <h3 className="font-serif text-lg text-slate-800">{t("matter.details")}</h3>
            <Field label={t("matter.client")}>
              {matter.client_id ? (
                <Link to={`/clients/${matter.client_id}`} data-testid="matter-client-link"
                  className="text-amber-700 hover:underline">
                  {matter.client}
                </Link>
              ) : (
                matter.client
              )}
            </Field>
            <Field label={t("matter.opposing_party")}>{matter.opposing_party}</Field>
            <Field label={t("matter.internal_ref")}>{matter.internal_ref}</Field>
            <Field label={t("matter.court_authority")}>
              {matter.court_authority && (
                <span className="inline-flex items-center gap-1.5">
                  <Landmark className="h-3.5 w-3.5 text-slate-400" /> {matter.court_authority}
                </span>
              )}
            </Field>
            <Field label={t("matter.court_case_number")}>{matter.court_case_number}</Field>
            <Field label={t("matter.matter_type")}>
              {matter.matter_type === "Other" && matter.matter_type_other
                ? matter.matter_type_other
                : t(`val.${matter.matter_type}`)}
            </Field>
            <Field label={t("matter.responsible_lawyer")}>{matter.responsible_lawyer}</Field>
            <Field label={t("matter.billing_type")}>{t(`val.${matter.billing_type || "Hourly"}`)}</Field>
            <Field label={t("matter.default_hourly_rate")}>
              {matter.default_hourly_rate ? `${fmtMoney(matter.default_hourly_rate)} ${t("time.per_hour")}` : "—"}
            </Field>
            <Field label={t("matter.notes")}>{matter.notes}</Field>
            <Field label={t("matter.dropbox_folder")}>
              {dropboxHref ? (
                <a
                  href={dropboxHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="dropbox-folder-link"
                  className="inline-flex items-center gap-1.5 text-slate-800 hover:text-amber-700 underline underline-offset-2 break-all"
                >
                  <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {t("matter.open_dropbox")}
                </a>
              ) : (
                "—"
              )}
            </Field>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="matter-financial-summary">
            <h3 className="font-serif text-lg text-slate-800 mb-4">{t("summary.title")}</h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">{t("summary.total_billable_time")}</span>
                <span className="font-semibold text-slate-800" data-testid="summary-total-billable-time">
                  {fmtDecimalHours(totals.total_billable_minutes)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("summary.value_billable_time")}</span>
                <span className="font-semibold text-slate-800" data-testid="summary-value-billable-time">
                  {fmtMoney(totals.value_billable_time)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("summary.unbilled_time")}</span>
                <span className="font-semibold text-amber-700" data-testid="summary-unbilled-time">
                  {fmtMoney(totals.unbilled_time_value)}
                </span>
              </div>
              <div className="flex justify-between border-t border-slate-100 pt-2.5">
                <span className="text-slate-500">{t("summary.total_billable_expenses")}</span>
                <span className="font-semibold text-slate-800" data-testid="summary-total-billable-expenses">
                  {fmtMoney(totals.total_billable_expenses)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{t("summary.unbilled_expenses")}</span>
                <span className="font-semibold text-amber-700" data-testid="summary-unbilled-expenses">
                  {fmtMoney(totals.unbilled_expenses)}
                </span>
              </div>
            </div>
          </div>
          <ExternalLinks matter={matter} settings={settings} onAdd={() => setEditMatter(true)} />
        </div>

        <div className="lg:col-span-8 space-y-8">
          <section data-testid="deadlines-section">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="font-serif text-xl text-slate-800">
                {t("deadlines.title")} <span className="text-sm text-slate-400 font-sans">({deadlines.length})</span>
              </h2>
              <div className="flex items-center gap-2">
                <Select value={deadlineTypeFilter} onValueChange={setDeadlineTypeFilter}>
                  <SelectTrigger data-testid="deadline-type-filter" className="h-9 w-48 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All" data-testid="deadline-type-filter-all">{t("deadlines.all_types")}</SelectItem>
                    {DEADLINE_TYPES.map((t2) => (
                      <SelectItem
                        key={t2}
                        value={t2}
                        data-testid={`deadline-type-filter-${t2.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {t(`val.${t2}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  data-testid="add-deadline-button"
                  onClick={() => setDeadlineDialog({ open: true, item: null })}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4 mr-1" /> {t("deadlines.add")}
                </Button>
              </div>
            </div>
            <div className="space-y-2.5">
              {deadlines.length === 0 && (
                <div className="text-sm text-slate-500" data-testid="no-deadlines">{t("deadlines.none")}</div>
              )}
              {deadlines.length > 0 && visibleDeadlines.length === 0 && (
                <div className="text-sm text-slate-500" data-testid="no-deadlines-of-type">
                  {t("deadlines.none_of_type")}
                </div>
              )}
              {visibleDeadlines.map((d) => (
                <ItemRow
                  key={d.id}
                  item={d}
                  kind="deadline"
                  onEdit={(item) => setDeadlineDialog({ open: true, item })}
                  onDelete={(item) => deleteItem("deadline", item)}
                  onToggle={(item) => toggleItem("deadline", item)}
                  onCalendar={(item) => setCalDeadline(item)}
                />
              ))}
            </div>
          </section>

          <section data-testid="tasks-section">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h2 className="font-serif text-xl text-slate-800">
                {t("tasks.title")} <span className="text-sm text-slate-400 font-sans">({tasks.length})</span>
              </h2>
              <Button
                size="sm"
                data-testid="add-task-button"
                onClick={() => setTaskDialog({ open: true, item: null })}
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Plus className="h-4 w-4 mr-1" /> {t("tasks.add")}
              </Button>
            </div>
            <div className="space-y-2.5">
              {tasks.length === 0 && (
                <div className="text-sm text-slate-500" data-testid="no-tasks">{t("tasks.none")}</div>
              )}
              {tasks.map((t) => (
                <ItemRow
                  key={t.id}
                  item={t}
                  kind="task"
                  onEdit={(item) => setTaskDialog({ open: true, item })}
                  onDelete={(item) => deleteItem("task", item)}
                  onToggle={(item) => toggleItem("task", item)}
                />
              ))}
            </div>
          </section>

          <TimeSection matter={matter} onChanged={load} />
          <ExpenseSection matter={matter} onChanged={load} />
        </div>
      </div>

      <MatterDialog
        open={editMatter}
        onOpenChange={setEditMatter}
        initial={matter}
        onSaved={() => {
          setEditMatter(false);
          load();
          toast.success(t("matter.updated"));
        }}
      />
      <DeadlineDialog
        open={deadlineDialog.open}
        onOpenChange={(o) => setDeadlineDialog({ open: o, item: null })}
        matterId={matter.id}
        initial={deadlineDialog.item}
        onSaved={() => {
          setDeadlineDialog({ open: false, item: null });
          load();
          toast.success(t(deadlineDialog.item ? "deadlines.updated" : "deadlines.added"));
        }}
      />
      <TaskDialog
        open={taskDialog.open}
        onOpenChange={(o) => setTaskDialog({ open: o, item: null })}
        matterId={matter.id}
        initial={taskDialog.item}
        onSaved={() => {
          setTaskDialog({ open: false, item: null });
          load();
          toast.success(t(taskDialog.item ? "tasks.updated" : "tasks.added"));
        }}
      />
      <CalendarDialog
        open={Boolean(calDeadline)}
        onOpenChange={(o) => !o && setCalDeadline(null)}
        deadline={calDeadline}
        matter={matter}
      />
    </div>
  );
}
